/**
 * DocumentHeader — spec iter185 (2026-02-27)
 *
 * Bandeau haut extrait de `pages/Documents.jsx` (Point 1c du backlog
 * technique). Contient :
 *   • Ligne 1 : titre "Documents" + sous-titre "N · vos documents" +
 *     scope switch (Perso/Globale) + boutons Corbeille + profil.
 *   • Ligne 2 : bandeau KPI (7 cartes égales — Total / En cours /
 *     Délai dépassé / À vérifier / Clôturé / Suspendu / Types /
 *     Document new).
 *
 * Contrainte : zéro changement visuel. La constante `DOCS_KPIS` est
 * exportée pour rester utilisable depuis `Documents.jsx` (résolution
 * des libellés lors du filtrage).
 */
import { FileText, LayoutGrid, Loader, AlertTriangle, Eye, LockIcon as LockIcon, PauseCircle, Tags, UserCircle2, Globe2, Palette, FileDown, Archive, Trash2, XCircle } from "lucide-react";
import ScopeSwitch from "@/components/ui/ScopeSwitch";
import HeaderPill from "@/components/ui/HeaderPill";
import KpiSelectedBadge from "@/components/ui/KpiSelectedBadge";
import { MODULE_COLORS } from "@/lib/moduleColors";

const DOCS_MODULE_COLOR = MODULE_COLORS.documents || "#2B579A";

// Spec iter185 (2026-02-27) — DOCS_KPIS déplacé depuis Documents.jsx.
// Ré-exporté pour un usage transverse (résolution de libellés depuis
// Documents.jsx pour l'affichage des filtres actifs).
export const DOCS_KPIS = [
  { key: "total",         label: "Total",         color: "#2B579A", Icon: LayoutGrid,    filled: true  },
  { key: "en_cours",      label: "En cours",      color: "#D85A30", Icon: Loader,        filled: false },
  // Spec 2026-02-XX rev9 — statut automatique « Délai dépassé » ; s'affiche
  // dès qu'un document a au moins une étape non-fermée avec deadline dans
  // le passé (calculé côté serveur via effective_status). Positionné entre
  // « En cours » et « À vérifier » — l'urgence prime visuellement (choix
  // utilisateur validé Feb 2026).
  { key: "delai_depasse", label: "Délai dépassé", color: "#EF4444", Icon: AlertTriangle, filled: false },
  { key: "termine",       label: "À vérifier",    color: "#8B5CF6", Icon: Eye,           filled: false, sumKeys: ["terminee", "termine"] },
  { key: "cloture",       label: "Clôturé",       color: "var(--status-cloture-docs)", Icon: LockIcon,          filled: false },
  { key: "suspendu",      label: "Suspendu",      color: "#B8623F", Icon: PauseCircle,   filled: false },
  // Spec iter207 (2026-02-28) — Nouveau statut final « Annulé »
  // (spec iter206 pt.2). Icône XCircle grise pour signaler
  // l'inactivité irréversible.
  { key: "annule",        label: "Annulés",       color: "#6B7280", Icon: XCircle,       filled: false },
];

// ---------------------------------------------------------------------------
// DocumentsHeader — 2 lignes distinctes (spec 2026-07-17 septies)
//   • Ligne 1 : titre + sous-titre + [Corbeille, Archives, profil]
//   • Ligne 2 : bandeau KPI toute la largeur (7 cartes égales)
// ---------------------------------------------------------------------------
export default function DocumentsHeader({
  stats, activeKey, suspendedActive, onSelectKpi,
  onNavigateTypes, onOpenTrash, onCreate, canCreate, hasTypes, hideCreate = false,
  isAdminLevel, viewScope, onViewScopeChange, headerProfileSlot,
}) {
  const s = stats || {};
  const total = s.total ?? 0;
  const isGlobal = viewScope === "global";
  return (
    <div className="flex flex-col gap-3" data-testid="documents-header">
      {/* Ligne 1 — Titre + actions + profil */}
      <div
        className="flex items-center gap-3"
        style={{ background: "#1E1E1E", border: "1px solid #2A2A2A", borderRadius: 12, padding: "10px 16px" }}
      >
        <div className="flex items-center gap-2.5">
          <div className="rounded-lg flex items-center justify-center shrink-0"
            style={{ width: 32, height: 32, background: "linear-gradient(135deg, #3568B0 0%, #1A3358 100%)",
                     boxShadow: "0 2px 6px rgba(0,0,0,0.3)" }}
            data-testid="documents-header-icon">
            <FileText size={18} color="#FFFFFF" strokeWidth={2.2} />
          </div>
          <div className="flex flex-col leading-tight">
            <span className="text-base font-bold flex items-center gap-1.5">
              Documents
              {isGlobal ? (
                <Globe2 size={14} strokeWidth={2.2} style={{ color: DOCS_MODULE_COLOR }} data-testid="docs-scope-icon-global">
                  <title>Vue globale — tous les documents</title>
                </Globe2>
              ) : (
                <UserCircle2 size={14} strokeWidth={2.2} style={{ color: "#3B9CE0" }} data-testid="docs-scope-icon-mine">
                  <title>Vue personnelle — vos documents</title>
                </UserCircle2>
              )}
            </span>
            <span className="text-[10px] text-muted-foreground">
              {total} · {isGlobal ? "vue globale" : "vos documents"}
            </span>
          </div>
        </div>

        <div className="flex-1" />

        <div className="flex items-center gap-2">
          <ScopeSwitch
            module="documents"
            value={viewScope}
            onChange={(next) => onViewScopeChange?.(next)}
            testid="docs-scope-toggle"
          />
          <HeaderPill
            icon={Trash2} iconBg="#EF4444" label="Corbeille"
            onClick={onOpenTrash}
            title="Corbeille" testid="docs-header-trash"
          />
        </div>

        {headerProfileSlot && (
          <div className="ml-3">{headerProfileSlot}</div>
        )}
      </div>

      {/* Ligne 2 — Bandeau KPI (7 cartes égales) */}
      <div
        className="flex items-stretch"
        style={{ background: "#1E1E1E", border: "1px solid #2A2A2A", borderRadius: 12, padding: "10px 12px", gap: 6 }}
      >
        <div className="flex-1 grid" style={{ gridTemplateColumns: "repeat(9, minmax(0, 1fr))", gap: 6 }}
             data-testid="docs-mini-kpis">
          {DOCS_KPIS.map(({ key, label, color, Icon, filled, sumKeys }) => {
            const value = sumKeys ? sumKeys.reduce((a, k) => a + (s[k] ?? 0), 0) : (s[key] ?? 0);
            const isActive = key === "total"
              ? (!activeKey && !suspendedActive)
              : key === "suspendu"
                ? !!suspendedActive
                : activeKey === key;
            const handle = () => onSelectKpi?.(key === "total" ? "" : (isActive ? "" : key));
            // Spec 2026-02-XX rev4 (Feb 2026) — KPI cards avec LISERÉ COLORÉ
            // gauche 3px (au lieu de fond plein pour la carte Total, plus
            // cohérent avec la charte Documents et le liseré de ligne du
            // tableau). Le liseré prend la couleur du KPI, le fond reste
            // uniformément sombre — l'état actif renforce la bordure + le
            // liseré (glow). L'ancien mode "filled" est abandonné.
            //
            // Spec 2026-02-XX rev19 (Point N) — RÉINTRODUCTION du mode
            // "filled" UNIQUEMENT pour la carte Total : fond bleu plein
            // #2B579A + texte + icône + libellé blancs. Aligne le pattern
            // avec le module Tâches (Total en jaune plein #F9BC26 + texte
            // brun). Les 6 autres KPI conservent le liseré coloré.
            const cardStyle = isActive
              ? {
                  // Spec iter102 (Feb 2026) — KPI actif : fond PLEIN bleu
                  // module (MODULE_COLORS.documents) + pastille verte
                  // check top-right via <KpiSelectedBadge/>. Le liseré
                  // gauche disparaît pour laisser la couleur module dominer.
                  background: MODULE_COLORS.documents,
                  border: "1px solid #FFFFFF66",
                  color: "#FFFFFF",
                  boxShadow: "none",
                  textShadow: "0 1px 2px rgba(0,0,0,0.35)",
                }
              : (filled
                ? {
                    background: color,
                    border: "1px solid transparent",
                    color: "#FFFFFF",
                    boxShadow: "none",
                  }
                : {
                    background: "#18181a",
                    border: "1px solid #232326",
                    color: "#FFFFFF",
                    boxShadow: `inset 3px 0 0 0 ${color}CC`,
                  });
            return (
              <button
                type="button"
                key={key}
                onClick={handle}
                className="flex flex-col items-center justify-center rounded-md transition-all min-w-0"
                style={{ ...cardStyle, padding: (isActive || filled) ? "6px 4px" : "6px 4px 6px 8px", minHeight: 60, cursor: "pointer", position: "relative" }}
                onMouseEnter={(e) => { if (!isActive && !filled) e.currentTarget.style.background = "#1c1c1e"; }}
                onMouseLeave={(e) => { if (!isActive && !filled) e.currentTarget.style.background = "#18181a"; }}
                data-testid={`docs-mini-kpi-${key}`}
                title={label}
              >
                {isActive && key !== "total" && <KpiSelectedBadge testId={`docs-mini-kpi-${key}-selected`} />}
                <Icon size={13} strokeWidth={2.2} style={{ color: (isActive || filled) ? "#FFFFFF" : color }} />
                <span className="font-bold tabular-nums"
                  style={{ fontSize: 21, lineHeight: 1, marginTop: 4, color: "#FFFFFF" }}>
                  {value}
                </span>
                <span className="uppercase mt-0.5 truncate max-w-full block text-center"
                  style={{ fontSize: 10, letterSpacing: "0.03em",
                           color: (isActive || filled) ? "#FFFFFF" : "#9a9a95", fontWeight: 700 }}>
                  {label}
                </span>
              </button>
            );
          })}
          {/* Bouton "Types" — accès à Gérer les types (admins) */}
          {isAdminLevel && <button
            type="button"
            onClick={onNavigateTypes}
            className="flex flex-col items-center justify-center rounded-md transition-all"
            style={{
              background: "#232320", border: "1px solid #2A2A2A",
              color: "#EDEDED", padding: "6px 4px", minHeight: 60, cursor: "pointer",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = "#2a2a26"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "#232320"; }}
            data-testid="docs-btn-types"
            title="Gérer les types de documents"
          >
            {/* Spec 2026-07-19 bis — icône Tags agrandie (24px) pour
                égaler la présence visuelle des KPI numériques du bandeau.
                strokeWidth=2 = même trait que les autres icônes KPI. */}
            <Tags size={24} strokeWidth={2} style={{ color: "#D4537E" }} />
            <span aria-hidden="true" className="tabular-nums"
              style={{ fontSize: 21, lineHeight: 1, marginTop: 4, visibility: "hidden" }}>0</span>
            <span className="uppercase mt-0.5 whitespace-nowrap"
              style={{ fontSize: 10, letterSpacing: "0.03em", color: "#9a9a95", fontWeight: 700 }}>
              Types
            </span>
          </button>}
          {/* Bouton "+ Document" — création */}
          {/* Spec rev153 (2026-02-07) — bouton unifié via HeaderPill variant="kpi". */}
          {!hideCreate && <HeaderPill
            variant="kpi"
            iconBg="#2B579A"
            label="Document"
            onClick={onCreate}
            disabled={!canCreate}
            testid="docs-btn-new"
            title={canCreate ? "Nouveau document" : (!hasTypes ? "Créez d'abord un type de document" : "Permission requise")}
          />}
        </div>
      </div>
    </div>
  );
}
